// src/components/bot/BotFlowPreview.tsx
// 🎬 SIMPLIFIED BOT FLOW PREVIEW - Clean WhatsApp Preview
// ✅ Simple play button with WhatsApp chat simulation

import React, { useState, useEffect, useRef } from 'react';
import styles from './BotFlowPreview.module.css';
import logger from '../../../utils/logger';

/**
 * 📝 INTERFACE DEFINITIONS
 */
interface Question {
  id: string;
  question: string;
  type: string;
  required: boolean;
  interactive?: {
    type: 'list' | 'button';
    header?: { type: 'text'; text: string };
    body?: { text: string };
    action?: {
      button?: string;
      buttons?: Array<{type: string; reply: {id: string; title: string}}>;
      sections?: Array<{title: string; rows: Array<{id: string; title: string; description?: string}>}>;
    };
  };
  templateData?: any; // For template questions
}

interface BotFlowPreviewProps {
  botName: string;
  keywords: string[];
  questions: Question[];
  selectedTemplate: any;
  responseType: 'template' | 'questionnaire';
  onClose?: () => void;
}

interface ConversationStep {
  id: string;
  type: 'user' | 'bot' | 'template';
  content: string;
  timestamp: string;
  questionType?: string;
  interactive?: any;
  templateData?: any;
}

/**
 * 🎬 BOT FLOW PREVIEW COMPONENT
 * Complete implementation with all missing features
 */
const BotFlowPreview: React.FC<BotFlowPreviewProps> = ({
  botName,
  keywords,
  questions,
  selectedTemplate,
  responseType,
  onClose
}) => {
  logger.log('🎬 BotFlowPreview: Component initialized with props:', {
    botName,
    keywordsCount: keywords.length,
    questionsCount: questions.length,
    responseType,
    templateSelected: !!selectedTemplate
  });

  // 🎯 SIMPLIFIED STATE
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [conversationSteps, setConversationSteps] = useState<ConversationStep[]>([]);
  const chatRef = useRef<HTMLDivElement>(null);

  /**
   * 🎯 GENERATE CONVERSATION FLOW
   */
  useEffect(() => {
    const generateFlow = () => {
      const steps: ConversationStep[] = [];
      const now = new Date();
      
      // 1. User sends keyword
      const triggerKeyword = keywords[0] || 'hello';
      steps.push({
        id: 'trigger',
        type: 'user',
        content: triggerKeyword,
        timestamp: formatTime(new Date(now.getTime() - (questions.length + 2) * 60000))
      });

      // 2. Process all questions (including template questions)
      questions.forEach((question, index) => {
        // Bot asks question or sends template
        if (question.type === 'template') {
          steps.push({
            id: `template-${question.id}`,
            type: 'template',
            content: question.question || 'Template Message',
            timestamp: formatTime(new Date(now.getTime() - (questions.length - index + 1) * 60000)),
            templateData: question.templateData
          });
        } else {
          steps.push({
            id: `question-${question.id}`,
            type: 'bot',
            content: question.question,
            timestamp: formatTime(new Date(now.getTime() - (questions.length - index + 1) * 60000)),
            questionType: question.type,
            interactive: question.interactive
          });

          // User responds (only for non-template questions)
          const response = generateSampleResponse(question);
          steps.push({
            id: `response-${question.id}`,
            type: 'user',
            content: response,
            timestamp: formatTime(new Date(now.getTime() - (questions.length - index + 0.5) * 60000))
          });
        }
      });

      setConversationSteps(steps);
    };

    generateFlow();
  }, [botName, keywords, questions, selectedTemplate, responseType]);

  /**
   * 🎯 GENERATE SAMPLE RESPONSES
   */
  const generateSampleResponse = (question: Question): string => {
    const responses = {
      text: 'John Doe',
      email: 'john.doe@example.com',
      phone: '+1 (555) 123-4567',
      number: '25',
      location: '📍 New York, NY, USA',
      'interactive-list': '✅ Selected: Option 1',
      'interactive-button': '🔘 Clicked: Yes'
    };
    return responses[question.type as keyof typeof responses] || 'Sample response';
  };

  /**
   * 🕒 FORMAT TIME
   * Format timestamp for chat display
   */
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  /**
   * 🎬 PLAY PREVIEW
   */
  const startPreview = () => {
    setIsPlaying(true);
    setCurrentStep(0);
    
    conversationSteps.forEach((step, index) => {
      setTimeout(() => {
        setCurrentStep(index + 1);
        
        // Auto-scroll to bottom with smooth behavior
        if (chatRef.current) {
          setTimeout(() => {
            chatRef.current?.scrollTo({
              top: chatRef.current.scrollHeight,
              behavior: 'smooth'
            });
          }, 100);
        }
        
        // Stop when finished
        if (index === conversationSteps.length - 1) {
          setIsPlaying(false);
        }
      }, index * 1500); // Fixed 1.5 second intervals
    });
  };

  const resetPreview = () => {
    setCurrentStep(0);
    setIsPlaying(false);
  };


  /**
   * 🎨 RENDER INTERACTIVE MESSAGE
   * Render WhatsApp interactive elements in preview
   */
  const renderInteractiveMessage = (step: ConversationStep) => {
    if (!step.interactive) return null;

    logger.log('🎨 BotFlowPreview: Rendering interactive message:', step.interactive.type);

    if (step.interactive.type === 'list') {
      return (
        <div className={styles.whatsappInteractiveList}>
          {step.interactive.header?.text && (
            <div className={styles.interactiveHeader}>
              {step.interactive.header.text}
            </div>
          )}
          {step.interactive.body?.text && (
            <div className={styles.interactiveBody}>
              {step.interactive.body.text}
            </div>
          )}
          <div className={styles.listButton}>
            📋 {step.interactive.action?.button || 'View Options'}
          </div>
          <div className={styles.listPreview}>
            {step.interactive.action?.sections?.map((section: any, index: number) => (
              <div key={index} className={styles.listSection}>
                <div className={styles.sectionTitle}>{section.title}</div>
                {section.rows?.slice(0, 2).map((row: any, rowIndex: number) => (
                  <div key={rowIndex} className={styles.listRow}>
                    {row.title}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (step.interactive.type === 'button') {
      return (
        <div className={styles.whatsappInteractiveButtons}>
          {step.interactive.header?.text && (
            <div className={styles.interactiveHeader}>
              {step.interactive.header.text}
            </div>
          )}
          {step.interactive.body?.text && (
            <div className={styles.interactiveBody}>
              {step.interactive.body.text}
            </div>
          )}
          <div className={styles.buttonsList}>
            {step.interactive.action?.buttons?.map((button: any, index: number) => (
              <div key={index} className={styles.whatsappButton}>
                🔘 {button.reply?.title || `Button ${index + 1}`}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  /**
   * 🎨 RENDER TEMPLATE MESSAGE
   */
  const renderTemplateMessage = (step: ConversationStep) => {
    const templateName = step.templateData?.name || step.templateData?.template?.name || 'WhatsApp Template';
    const templateBody = step.templateData?.template?.body || step.templateData?.body || step.content;
    
    return (
      <div className={styles.templateMessage}>
        <div className={styles.templateHeader}>
          📄 {templateName}
        </div>
        <div className={styles.templateContent}>
          {templateBody}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.previewOverlay}>
      <div className={styles.previewContainer}>
        
        {/* 🎯 SIMPLIFIED HEADER */}
        <div className={styles.previewHeader}>
          <div className={styles.headerLeft}>
            <h2>🎬 Bot Preview</h2>
            <div className={styles.botInfo}>
              <span className={styles.botName}>{botName || 'Your Bot'}</span>
              <span className={styles.stepCounter}>
                {currentStep}/{conversationSteps.length}
              </span>
            </div>
          </div>
          <div className={styles.headerControls}>
            <button 
              className={styles.playBtn}
              onClick={isPlaying ? resetPreview : startPreview}
              disabled={conversationSteps.length === 0}
              type="button"
            >
              {isPlaying ? '🔄 Reset' : '▶️ Play'}
            </button>
            {onClose && (
              <button className={styles.closeBtn} onClick={onClose} type="button">
                ✕
              </button>
            )}
          </div>
        </div>

        {/* 🎯 WHATSAPP CHAT VIEW */}
        <div className={styles.previewContent}>
          <div className={styles.chatView}>
              <div className={styles.whatsappContainer}>
                
                {/* WhatsApp Header */}
                <div className={styles.whatsappHeader}>
                  <div className={styles.contactInfo}>
                    <div className={styles.avatar}>🤖</div>
                    <div className={styles.contactDetails}>
                      <div className={styles.contactName}>{botName || 'Bot'}</div>
                      <div className={styles.contactStatus}>
                        {isPlaying ? 'typing...' : 'online'}
                      </div>
                    </div>
                  </div>
                  <div className={styles.headerActions}>
                    <span>⋮</span>
                  </div>
                </div>

                {/* Chat Messages */}
                <div className={styles.chatMessages} ref={chatRef}>
                  {conversationSteps.slice(0, currentStep).map((step, index) => (
                    <div key={step.id} className={styles.messageGroup}>
                      
                      {/* User Messages */}
                      {step.type === 'user' && (
                        <div className={styles.userMessage}>
                          <div className={styles.messageContent}>
                            {step.content}
                          </div>
                          <div className={styles.messageTime}>
                            {step.timestamp} ✓✓
                          </div>
                        </div>
                      )}

                      {/* Bot Messages */}
                      {step.type === 'bot' && (
                        <div className={styles.botMessage}>
                          <div className={styles.messageContent}>
                            {step.interactive ? (
                              renderInteractiveMessage(step)
                            ) : (
                              <div className={styles.textMessage}>
                                {step.content}
                              </div>
                            )}
                          </div>
                          <div className={styles.messageTime}>
                            {step.timestamp}
                          </div>
                        </div>
                      )}

                      {/* Template Messages */}
                      {step.type === 'template' && (
                        <div className={styles.botMessage}>
                          <div className={styles.messageContent}>
                            {renderTemplateMessage(step)}
                          </div>
                          <div className={styles.messageTime}>
                            {step.timestamp}
                          </div>
                        </div>
                      )}

                    </div>
                  ))}

                  {/* Typing Indicator */}
                  {isPlaying && (
                    <div className={styles.typingIndicator}>
                      <div className={styles.typingDots}>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  )}
                </div>

                {/* WhatsApp Input (Disabled) */}
                <div className={styles.whatsappInput}>
                  <div className={styles.inputContainer}>
                    <span className={styles.inputIcon}>😊</span>
                    <input 
                      type="text" 
                      placeholder="Type a message..." 
                      disabled 
                      className={styles.messageInput}
                    />
                    <span className={styles.inputIcon}>🎤</span>
                  </div>
                </div>

              </div>
            </div>
        </div>

      </div>
    </div>
  );
};

export default BotFlowPreview;